import { useCallback, useMemo, useState } from "react";

const DEFAULT_SORT_DIR = {
  text: "asc",
  number: "desc",
  money: "desc",
  percent: "desc",
  duration: "desc",
  date: "desc",
};

const normalizeTextValue = (value) => String(value ?? "").trim().toLowerCase();

const normalizeNumberValue = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : 0;
  }
  const raw = String(value).trim();
  if (!raw || raw === "--") return 0;
  const isNegative = raw.startsWith("(") && raw.endsWith(")");
  const cleaned = raw.replace(/[$,%()]/g, "").replace(/,/g, "");
  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed)) return 0;
  return isNegative ? -parsed : parsed;
};

const normalizeDateValue = (value) => {
  if (!value) return 0;
  const parsed = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDefaultSortDir = (type) => DEFAULT_SORT_DIR[type] || "asc";

const sortRows = (rows, sortState, columns, fallbackKey) => {
  if (!sortState?.key || !columns?.[sortState.key]) return rows;
  const { key, dir } = sortState;
  const column = columns[key];
  const direction = dir === "asc" ? 1 : -1;
  const withIndex = rows.map((row, index) => ({ row, index }));
  const getValue = column.getValue || ((row) => row?.[key]);
  const normalizeValue = (value) => {
    if (column.type === "text") return normalizeTextValue(value);
    if (column.type === "date") return normalizeDateValue(value);
    return normalizeNumberValue(value);
  };
  const getFallback =
    fallbackKey && columns[fallbackKey]
      ? (row) =>
          normalizeTextValue(
            (columns[fallbackKey].getValue || ((item) => item?.[fallbackKey]))(row)
          )
      : null;
  withIndex.sort((a, b) => {
    const aVal = normalizeValue(getValue(a.row));
    const bVal = normalizeValue(getValue(b.row));
    if (column.type === "text") {
      const diff = aVal.localeCompare(bVal, undefined, { numeric: true });
      if (diff !== 0) return diff * direction;
    } else {
      const diff = aVal - bVal;
      if (diff !== 0) return diff * direction;
    }
    if (getFallback) {
      const fallbackDiff = getFallback(a.row).localeCompare(getFallback(b.row), undefined, {
        numeric: true,
      });
      if (fallbackDiff !== 0) return fallbackDiff;
    }
    return a.index - b.index;
  });
  return withIndex.map((entry) => entry.row);
};

export default function useTableSort({
  rows,
  columns,
  defaultSortBy = "",
  defaultSortDir,
  fallbackKey,
}) {
  const [sortState, setSortState] = useState({
    key: defaultSortBy,
    dir: defaultSortDir || getDefaultSortDir(columns?.[defaultSortBy]?.type),
  });

  const sortedRows = useMemo(
    () => sortRows(rows || [], sortState, columns || {}, fallbackKey),
    [rows, sortState, columns, fallbackKey]
  );

  const handleSort = useCallback(
    (key) => {
      if (!columns?.[key]) return;
      setSortState((prev) => {
        if (prev.key === key) {
          return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
        }
        return { key, dir: getDefaultSortDir(columns[key].type) };
      });
    },
    [columns]
  );

  return { sortState, sortedRows, handleSort, setSortState };
}

